// Verbatim port of legacy
// frontend/src/pages/Category/PantryCategory/storeDomain.ts. No code-shape
// changes. Returns the hostname portion of a store URL with any leading
// "www." stripped — used by the page header / drawer to render a clean
// domain label for the active store.

/** Hostname for display from store URL (e.g. https://shop.example.com/path → shop.example.com). */
export function storeDomainFromUrl(url: string | undefined): string {
  if (!url?.trim()) return ''
  const t = url.trim()
  try {
    const withProto = t.includes('://') ? t : `https://${t}`
    const u = new URL(withProto)
    return u.hostname.replace(/^www\./i, '')
  } catch {
    return (
      t
        .replace(/^https?:\/\//i, '')
        .split('/')[0]
        ?.split('?')[0] ?? ''
    )
  }
}

/**
 * Returns a shortened display label for a hostname. If the hostname is longer
 * than `maxLen` characters, it is truncated to `maxLen` chars and an ellipsis
 * is appended. Otherwise the original hostname is returned unchanged.
 *
 * Used by slug-prefix labels in the Category drawer / table / new-dialog where
 * the full URL would otherwise overflow the available width.
 */
export function shortenDomain(domain: string, maxLen = 14): string {
  if (!domain) return ''
  if (domain.length <= maxLen) return domain
  return `${domain.slice(0, maxLen)}...`
}

/**
 * Storefront URL prefix shown before the slug input:
 * `https://<domain>/products/category/` plus `<parentFullSlug>/` for a
 * subcategory. `short` truncates the hostname via `shortenDomain` for the
 * label; pass `false` for the tooltip. Empty when the store has no domain.
 */
export function categoryUrlPrefix(
  storeDomain: string,
  parentFullSlug: string,
  short = true,
): string {
  if (!storeDomain) return ''
  const host = short ? shortenDomain(storeDomain) : storeDomain
  const parentPath = parentFullSlug ? `${parentFullSlug}/` : ''
  return `https://${host}/products/category/${parentPath}`
}
